import React from 'react';
import { Chip } from '@mui/material';
import dayjs from 'dayjs';
import {
  stringCustomOperators,
  numberCustomOperators,
} from '@Utils/Validations/HelperFunctions/CustomFilters';
import { getPlanStatusLabel, PLAN_TYPES } from '@Utils/planType';

export const getExemptionStatusColor = (status) => {
  switch (status) {
    case 'PENDING_LOCAL_RISK_ASSESSMENT':
      return 'info';
    case 'SUBMITTED':
      return 'warning';
    case 'APPROVED':
      return 'success';
    case 'REJECTED':
      return 'error';
    case 'WAITING_FOR_SUPPORT':
      return 'secondary';
    default:
      return 'default';
  }
};

/**
 * The default column set for every exemption request listing: identity, what it concerns, who asked
 * and where it stands. The rest stay one click away in the grid's Columns menu.
 */
export const DEFAULT_EXEMPTION_COLUMN_VISIBILITY = {
  requestId: false,
  businessGroup: false,
  exceptionStartDate: false,
  exceptionEndDate: false,
  lastUpdatedAt: false,
};

const formatDate = (dateStr) => (dateStr ? dayjs(dateStr).format('MM/DD/YYYY') : '-');

const dateColumn = (field, headerName) => ({
  field,
  headerName,
  flex: 0.9,
  minWidth: 120,
  type: 'date',
  valueGetter: (params) => (params.row[field] ? new Date(params.row[field]) : null),
  valueFormatter: (params) => (params.value ? formatDate(params.value) : '-'),
});

/**
 * Columns shared by every Exemptions Hub. Field names match ExitPlanResponseDto and the
 * filter aliases registered in ExitPlanFilterServiceImpl (requestId, submittedByEmail, ...).
 */
export const buildExemptionColumns = ({ actionsWidth = 130 } = {}) => [
  { field: 'id', headerName: 'Exemption ID', flex: 0.8, minWidth: 110, type: 'number', filterOperators: numberCustomOperators },
  { field: 'requestId', headerName: 'Exit Request', flex: 0.8, minWidth: 110, type: 'number', filterOperators: numberCustomOperators },
  {
    field: 'ingredientName',
    headerName: 'Ingredient',
    flex: 1.4,
    minWidth: 160,
    type: 'string',
    filterOperators: stringCustomOperators,
    renderCell: (params) => (
      <div>
        <div>{params.row.ingredientName}</div>
        <div style={{ fontSize: '0.75rem', color: 'gray' }}>CAS: {params.row.ingredientCasNumber}</div>
      </div>
    ),
  },
  {
    field: 'businessGroup',
    headerName: 'BG',
    flex: 0.7,
    minWidth: 90,
    type: 'string',
    sortable: false,
    filterable: false,
  },
  { field: 'categoryName', headerName: 'Category', flex: 1, minWidth: 120, type: 'string', filterOperators: stringCustomOperators },
  {
    // spec A2: the requestor is the Regulatory member who created the exit request
    field: 'requestorEmail',
    headerName: 'Requestor',
    flex: 1.2,
    minWidth: 160,
    type: 'string',
    sortable: false,
    filterable: false,
    valueGetter: (params) => params.row.regulatoryOwner?.email || '-',
  },
  {
    field: 'submittedByEmail',
    headerName: 'Submitted By (R&D)',
    flex: 1.2,
    minWidth: 170,
    type: 'string',
    sortable: false,
    filterOperators: stringCustomOperators,
    valueGetter: (params) => params.row.submittedBy?.email || '-',
  },
  dateColumn('exceptionStartDate', 'Exception Start'),
  dateColumn('exceptionEndDate', 'Exception End'),
  {
    field: 'status',
    headerName: 'Status',
    flex: 1.2,
    minWidth: 190,
    type: 'string',
    filterOperators: stringCustomOperators,
    renderCell: (params) => (
      <Chip
        label={getPlanStatusLabel(params.row.status, PLAN_TYPES.EXEMPTION_REQUEST)}
        color={getExemptionStatusColor(params.row.status)}
        size="small"
      />
    ),
  },
  {
    field: 'approvalProgress',
    headerName: 'Approvals',
    flex: 0.7,
    minWidth: 90,
    type: 'number',
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const { approvedCount = 0, totalApprovers = 0 } = params.row;
      const complete = totalApprovers > 0 && approvedCount >= totalApprovers;
      return <Chip label={`${approvedCount}/${totalApprovers}`} color={complete ? 'success' : 'default'} size="small" variant="outlined" />;
    },
  },
  dateColumn('lastUpdatedAt', 'Last Updated'),
  { field: 'actions', headerName: 'Actions', sortable: false, filterable: false, width: actionsWidth, disableReorder: true },
];
